import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { BarChart2, ArrowLeft, Download } from 'lucide-react';
import { reachConfigAPI, reachAssessmentsAPI, ReachEngagementConfig, ReachAssessment } from '../../services/reachService';
import { ConfigGate } from '../../components/reach/common/ConfigGate';
import { ResultsHeatMap } from '../../components/reach/results-dashboard/ResultsHeatMap';
import { ResultsGapChart } from '../../components/reach/results-dashboard/ResultsGapChart';
import { ResultsRespondentTable } from '../../components/reach/results-dashboard/ResultsRespondentTable';
import { toast } from 'sonner';

type Layer = 'individual' | 'organisational';

const ResultsDashboardPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [config, setConfig] = useState<ReachEngagementConfig | null | undefined>(undefined);
  const [assessments, setAssessments] = useState<ReachAssessment[]>([]);
  const [layer, setLayer] = useState<Layer>('individual');

  useEffect(() => {
    Promise.all([
      reachConfigAPI.get(projectId!).then(r => setConfig(r.data.config)).catch(() => setConfig(null)),
      reachAssessmentsAPI.list(projectId!).then(r => setAssessments(r.data.assessments)).catch(() => {})
    ]);
  }, [projectId]);

  const handleDelete = async (id: string) => {
    try {
      await reachAssessmentsAPI.delete(projectId!, id);
      setAssessments(prev => prev.filter(a => a.id !== id));
      toast.success('Assessment removed');
    } catch {
      toast.error('Failed to remove assessment');
    }
  };

  const exportCSV = () => {
    const header = ['Name', 'Role', 'Type', 'Ind-R', 'Ind-E', 'Ind-A', 'Ind-C', 'Ind-H', 'Org-R', 'Org-E', 'Org-A', 'Org-C', 'Org-H', 'Date'];
    const rows = assessments.map(a => [
      a.respondentName, a.role, a.type,
      a.individual.r, a.individual.e, a.individual.a, a.individual.c, a.individual.h,
      a.organisational.r, a.organisational.e, a.organisational.a, a.organisational.c, a.organisational.h,
      a.timestamp
    ]);
    const csv = [header, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'reach-assessments.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  if (config === undefined) return (
    <div className="flex items-center justify-center h-64 text-slate-400 font-display">Loading…</div>
  );

  return (
    <div className="flex-1 overflow-y-auto p-8 font-display">
      <div className="w-full max-w-[1400px] mx-auto flex flex-col gap-6">
        <header className="flex items-center justify-between gap-4 border-b border-slate-200 pb-6">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate(`/projects/${projectId}/reach`)} className="text-slate-400 hover:text-navy transition-colors">
              <ArrowLeft size={20} />
            </button>
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary">
              <BarChart2 size={22} />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-navy">Results Dashboard</h1>
              <p className="text-slate-500 text-sm font-medium">{assessments.length} assessment{assessments.length !== 1 ? 's' : ''}</p>
            </div>
          </div>
          {assessments.length > 0 && (
            <button onClick={exportCSV}
              className="flex items-center gap-2 rounded-xl h-10 px-4 border border-slate-200 bg-white text-slate-600 text-sm font-bold hover:bg-slate-50 shadow-sm transition-colors">
              <Download size={16} /> Export CSV
            </button>
          )}
        </header>

        <ConfigGate hasConfig={!!config}>
          {/* Layer toggle */}
          <div className="flex rounded-xl border border-[#e2e8f0] overflow-hidden w-64">
            {(['individual', 'organisational'] as const).map(l => (
              <button key={l} onClick={() => setLayer(l)}
                className={`flex-1 py-2 text-xs font-semibold transition-all capitalize ${
                  layer === l ? 'bg-primary text-white' : 'bg-white text-slate-500 hover:bg-slate-50'
                }`}>
                {l}
              </button>
            ))}
          </div>

          {/* Heat map */}
          <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
            <div className="px-5 py-4 border-b border-[#e2e8f0]">
              <h2 className="text-sm font-bold text-navy">REACH Score by Segment</h2>
              <p className="text-xs text-slate-500 mt-0.5">Average scores 1–4 per role type and REACH stage</p>
            </div>
            <div className="p-5">
              <ResultsHeatMap assessments={assessments} layer={layer} />
            </div>
          </div>

          {/* Gap chart */}
          <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
            <div className="px-5 py-4 border-b border-[#e2e8f0]">
              <h2 className="text-sm font-bold text-navy">Individual vs Organisational Gap</h2>
              <p className="text-xs text-slate-500 mt-0.5">Average across all respondents per stage</p>
            </div>
            <div className="p-5">
              <ResultsGapChart assessments={assessments} />
            </div>
          </div>

          {/* Respondent table */}
          <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
            <div className="px-5 py-4 border-b border-[#e2e8f0]">
              <h2 className="text-sm font-bold text-navy">Respondents</h2>
            </div>
            <ResultsRespondentTable assessments={assessments} onDelete={handleDelete} />
          </div>
        </ConfigGate>
      </div>
    </div>
  );
};

export default ResultsDashboardPage;
